/**
 * AgendamentoLocaisSection - Locais Planejados section for AgendamentoTab
 */
import React from 'react';
import { Plus, X, MapPin, User as UserIcon, Phone } from 'lucide-react';
import type { LocalContagem } from '../../types/estabelecimento';

interface AgendamentoLocaisSectionProps {
  selectedHospitalId: number;
  loadingLocais: boolean;
  locaisContagemDisponiveis: LocalContagem[];
  locaisContagemSelecionados: LocalContagem[];
  selectedLocalId: number;
  setSelectedLocalId: (id: number) => void;
  handleAddLocal: () => void;
  handleRemoveLocal: () => void;
  isReadOnly: boolean;
}

export const AgendamentoLocaisSection: React.FC<AgendamentoLocaisSectionProps> = ({
  selectedHospitalId,
  loadingLocais,
  locaisContagemDisponiveis,
  locaisContagemSelecionados,
  selectedLocalId,
  setSelectedLocalId,
  handleAddLocal,
  handleRemoveLocal,
  isReadOnly,
}) => {
  return (
    <div className="mb-4">
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg p-4 border-2 border-emerald-200 dark:border-emerald-700">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          <MapPin size={18} className="text-emerald-600 dark:text-emerald-400" />
          Locais Planejados
          {loadingLocais && (
            <span className="ml-2 text-xs text-blue-500 font-normal">Carregando...</span>
          )}
        </label>

        {!selectedHospitalId ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            Selecione um hospital para ver os locais disponíveis
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-3">
              <select
                value={selectedLocalId}
                onChange={(e) => setSelectedLocalId(Number(e.target.value))}
                disabled={isReadOnly || loadingLocais}
                className="flex-1 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loadingLocais ? (
                  <option value="0">Carregando locais...</option>
                ) : locaisContagemDisponiveis.length === 0 ? (
                  <option value="0">Nenhum local cadastrado para este hospital</option>
                ) : (
                  <>
                    <option value="0">Selecione um local...</option>
                    {locaisContagemDisponiveis
                      .filter(l => !locaisContagemSelecionados.some(s => s.id === l.id))
                      .map((local) => (
                        <option key={local.id} value={local.id}>
                          {local.local} {local.responsavel ? `(${local.responsavel})` : ''}
                        </option>
                      ))}
                  </>
                )}
              </select>
              <button
                onClick={handleAddLocal}
                disabled={isReadOnly || !selectedLocalId}
                className="flex items-center space-x-1 px-3 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <Plus size={16} />
                <span>Adicionar</span>
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {locaisContagemSelecionados.length === 0 ? (
                <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                  Nenhum local selecionado para esta contagem
                </span>
              ) : (
                locaisContagemSelecionados.map((local) => (
                  <div
                    key={local.id}
                    className="inline-flex items-center gap-3 px-3 py-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg border border-emerald-200 dark:border-emerald-700"
                  >
                    <div className="flex items-center gap-1.5">
                      <MapPin size={14} className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                      <span className="font-medium text-gray-900 dark:text-white text-sm">{local.local}</span>
                    </div>

                    {local.responsavel && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                          <UserIcon size={12} className="flex-shrink-0" />
                          <span>{local.responsavel}</span>
                        </div>
                      </>
                    )}

                    {(local.telefone || local.ramal) && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                          <Phone size={12} className="flex-shrink-0" />
                          <span>
                            {local.telefone}
                            {local.telefone && local.ramal && ' • '}
                            {local.ramal && `R.${local.ramal}`}
                          </span>
                        </div>
                      </>
                    )}

                    {!isReadOnly && (
                      <button
                        onClick={() => { setSelectedLocalId(local.id); handleRemoveLocal(); }}
                        className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors ml-1"
                        title="Remover local"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
